import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DOCUMENT_TYPES } from "~/config/documents";
import { fetchProjectData } from "~/slices/projectSlice";
import type { AppDispatch, RootState } from "~/store";

const FALLBACK_TIMEOUTS = [180_000, 240_000, 300_000] as const;

export const useGenerationFallback = (): void => {
  const dispatch = useDispatch<AppDispatch>();
  const outputs = useSelector((state: RootState) => state.auth.currentProject?.outputs);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  // fetchProjectDataが失敗してoutputsが変わらなくてもeffectを再実行するためのカウンター
  const [retryTrigger, setRetryTrigger] = useState(0);

  useEffect(() => {
    const hasGeneratingDoc = outputs
      ? Object.keys(DOCUMENT_TYPES).some((key) => outputs[key]?.isGenerating === true)
      : false;

    if (!hasGeneratingDoc) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      attemptRef.current = 0;
      return;
    }

    if (timerRef.current) return;

    const attempt = attemptRef.current;
    if (attempt >= FALLBACK_TIMEOUTS.length) return;

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      attemptRef.current += 1;
      console.warn(
        `[useGenerationFallback] WebPubSub fallback triggered (attempt ${attempt + 1}/${FALLBACK_TIMEOUTS.length})`,
      );
      dispatch(fetchProjectData()).finally(() => {
        // outputsが変わらなくても次のタイマーをスケジュールするためeffectを再トリガー
        setRetryTrigger((prev) => prev + 1);
      });
    }, FALLBACK_TIMEOUTS[attempt]);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [outputs, dispatch, retryTrigger]);
};
